/**
 * v2's account settings: display name, email, password.
 *
 * v1 has a ProfileModal that does the same job, and this does NOT import it.
 * The two are separate applications that happen to share a repository and an
 * authentication endpoint; a shared component would mean every v2 change to
 * this dialog landing in v1 unannounced, and v1 is in maintenance while v2
 * moves. The auth STORE is shared, because the session genuinely is one
 * thing - only the presentation is duplicated, deliberately.
 */

import { useState } from 'react';
import { useAuthStore } from '../../store/auth';
import {
  FONT_SCALES,
  FONT_SCALE_LABELS,
  getFontScale,
  setFontScale,
  getTheme,
  setTheme,
  THEMES,
  THEME_LABELS,
  THEME_NOTES,
  THEME_SWATCH,
  type FontScale,
  type Theme,
} from './displayPrefs';

/** Which themes are dark - grouped apart in the picker. */
const DARK_THEMES = new Set<Theme>(['dark', 'graphite', 'terminal']);
const THEME_GROUPS: [string, Theme[]][] = [
  ['Light paper', THEMES.filter((t) => !DARK_THEMES.has(t))],
  ['Dark', THEMES.filter((t) => DARK_THEMES.has(t))],
];

type Message = { kind: 'ok' | 'error'; text: string } | null;

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelCls = 'block text-xs font-medium text-gray-600';
const buttonCls =
  'rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40';

function Note({ message }: { message: Message }) {
  if (!message) return null;
  return (
    <p
      role="status"
      className={`text-xs ${message.kind === 'ok' ? 'text-green-700' : 'text-red-700'}`}
    >
      {message.text}
    </p>
  );
}

export function AccountSettingsV2({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const changePassword = useAuthStore((s) => s.changePassword);

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [profileNote, setProfileNote] = useState<Message>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [fontScale, setScale] = useState<FontScale>(getFontScale);
  const [theme, setThemeState] = useState<Theme>(getTheme);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [passwordNote, setPasswordNote] = useState<Message>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = async () => {
    setProfileNote(null);
    setSavingProfile(true);
    try {
      await updateProfile({ name: name || undefined, email: email || undefined });
      setProfileNote({ kind: 'ok', text: 'Saved.' });
    } catch (err) {
      setProfileNote({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Could not save your profile.',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    setPasswordNote(null);
    if (next !== confirm) {
      setPasswordNote({ kind: 'error', text: 'The new passwords do not match.' });
      return;
    }
    setSavingPassword(true);
    try {
      await changePassword(current, next);
      setPasswordNote({ kind: 'ok', text: 'Password changed.' });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setPasswordNote({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Could not change your password.',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Account settings"
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Account settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-lg leading-none text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="account-name">
              Name
            </label>
            <input
              id="account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 ${inputCls}`}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="account-email">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 ${inputCls}`}
            />
          </div>
          <Note message={profileNote} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void saveProfile()}
              disabled={savingProfile || (!name && !email)}
              className={buttonCls}
            >
              {savingProfile ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <hr className="my-5 border-gray-200" />

        <h3 className="mb-3 text-xs font-semibold text-gray-900">Display</h3>
        <div className="mb-4 space-y-2">
          <span className={labelCls}>Theme</span>
          {/* Each theme is shown as a miniature page setting a real figure in its
              own ink and paper, so the choice is made on what the numbers will
              look like rather than on a name. Light papers first, then dark. */}
          {THEME_GROUPS.map(([group, options]) => (
            <div key={group} role="group" aria-label={`${group} themes`}>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500">{group}</div>
              <div className="grid grid-cols-4 gap-2">
                {options.map((option) => {
                  const swatch = THEME_SWATCH[option];
                  const chosen = theme === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={chosen}
                      title={THEME_NOTES[option]}
                      onClick={() => {
                        setThemeState(option);
                        setTheme(option);
                      }}
                      className={[
                        'rounded-lg border p-1.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                        chosen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-400',
                      ].join(' ')}
                    >
                      <span
                        aria-hidden
                        className="block rounded p-1.5"
                        style={{ background: swatch.canvas }}
                      >
                        <span
                          className="block rounded-sm px-1.5 py-1"
                          style={{ background: swatch.surface, border: `1px solid ${swatch.rule}` }}
                        >
                          <span
                            className="block h-1 w-8 rounded-full opacity-70"
                            style={{ background: swatch.ink }}
                          />
                          <span
                            className="mt-1 block border-t pt-0.5 text-right font-mono text-[10px] leading-tight"
                            style={{ color: swatch.ink, borderColor: swatch.rule }}
                          >
                            1,421,614
                          </span>
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-xs font-medium text-gray-900">
                        {THEME_LABELS[option]}
                      </span>
                      <span className="block truncate text-[10px] text-gray-500">{THEME_NOTES[option]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <span className={labelCls}>Text size</span>
          {/* Applied immediately rather than on a Save, so the effect of the
              choice is the feedback. It is stored per browser. */}
          <div className="flex gap-2" role="group" aria-label="Text size">
            {FONT_SCALES.map((scale) => (
              <button
                key={scale}
                type="button"
                aria-pressed={fontScale === scale}
                onClick={() => {
                  setScale(scale);
                  setFontScale(scale);
                }}
                className={[
                  'rounded border px-3 py-1.5 text-sm',
                  fontScale === scale
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {FONT_SCALE_LABELS[scale]}
              </button>
            ))}
          </div>
        </div>

        <hr className="my-5 border-gray-200" />

        <h3 className="mb-3 text-xs font-semibold text-gray-900">Change password</h3>
        <div className="space-y-3">
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputCls}
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputCls}
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputCls}
          />
          <Note message={passwordNote} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void savePassword()}
              disabled={savingPassword || !current || !next || !confirm}
              className={buttonCls}
            >
              {savingPassword ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
